/**
 * Incluir em lote — as posições de uma central a partir de um `.txt`.
 *
 * **É a volta de uma tela do sistema antigo** (`Sistema/Views/RelesFusiveis/Tabela.cshtml`,
 * botão "Incluir em Lote"), pedida por quem cadastra. A pessoa monta o arquivo fora do
 * sistema, uma posição por linha, e o sistema só reconhece:
 *
 *     F01|10A|Buzina esquerda
 *     F02 | 15A | Farol alto direito
 *
 * O formato é o mesmo de antes, e o preenchimento também: o código vai para Designação
 * **e** Definição, a corrente para Corrente, o texto para Função, e Terminal nasce `0` —
 * que é o que 20.396 das 26.040 linhas da base têm, porque foi assim que o lote antigo
 * gravou. A posição (2413, TGEX…) não vem no arquivo: é dada aqui, uma vez para todas.
 *
 * O que mudou do antigo, todas correções:
 * - linha de relé (`R1|…`, `K3|…`) também entra — o antigo só aceitava as que começam com
 *   `F` maiúsculo e descartava o resto calado; agora a tela diz o que ignorou;
 * - o texto é aparado (havia `' 20A'` gravado) e a quebra de linha do Windows não suja a
 *   função;
 * - arquivo salvo como ANSI pelo Bloco de Notas não perde os acentos;
 * - a gravação é **toda ou nada**, e o "deu certo" só aparece se deu.
 */

import { useState } from "react"
import { type ItemDeReles, TETO_DO_ITEM_DE_RELES, usarGravar } from "@/api/outrosProjetos"
import { Botao, mensagem } from "./pecas"

export type LinhaDoTxt = { numero: number; codigo: string; corrente: string; funcao: string }

export type LeituraDoTxt = {
  linhas: LinhaDoTxt[]
  /** Linhas com texto que não estão no formato — títulos, cabeçalhos, erros de digitação. */
  ignoradas: { numero: number; texto: string }[]
}

/**
 * Reconhece as linhas `código|corrente|função`.
 *
 * Vale a linha com **exatamente duas** barras cujo código começa com letra e tem algum
 * número (F01, R3, K12, F32A). O número é o que separa uma posição de um cabeçalho
 * como `Fusível|Corrente|Função`, que também tem duas barras.
 */
export function lerLinhasDoTxt(texto: string): LeituraDoTxt {
  const linhas: LinhaDoTxt[] = []
  const ignoradas: LeituraDoTxt["ignoradas"] = []

  texto.replace(/^﻿/, "").split(/\r\n|\r|\n/).forEach((bruta, i) => {
    const t = bruta.trim()
    if (!t) return
    const partes = t.split("|").map((p) => p.trim())
    const codigo = partes[0] ?? ""
    if (partes.length === 3 && /^[A-Za-z]/.test(codigo) && /\d/.test(codigo)) {
      linhas.push({ numero: i + 1, codigo, corrente: partes[1]!, funcao: partes[2]! })
    } else {
      ignoradas.push({ numero: i + 1, texto: t })
    }
  })

  return { linhas, ignoradas }
}

/**
 * Lê o arquivo como UTF-8 e, se não for, como Windows-1252 — o "ANSI" do Bloco de Notas.
 * Ler tudo como UTF-8 trocava "Função" por "Fun��o" sem aviso.
 */
export function lerArquivoDeTexto(arquivo: Blob): Promise<string> {
  return new Promise((resolver, recusar) => {
    const leitor = new FileReader()
    leitor.onerror = () => recusar(leitor.error ?? new Error("Não foi possível ler o arquivo."))
    leitor.onload = () => {
      const bytes = leitor.result as ArrayBuffer
      try {
        resolver(new TextDecoder("utf-8", { fatal: true }).decode(bytes))
      } catch {
        resolver(new TextDecoder("windows-1252").decode(bytes))
      }
    }
    leitor.readAsArrayBuffer(arquivo)
  })
}

type Posicao = Omit<ItemDeReles, "id">

/** O preenchimento do lote antigo, campo a campo. Ver a nota do módulo. */
function paraPosicao(l: LinhaDoTxt, posicao: string): Posicao {
  return {
    identificador: posicao, designacao: l.codigo, definicao: l.codigo, corrente: l.corrente,
    funcao: l.funcao, terminal: "0", cor: "", restricao: "", local: "", componente: "",
  }
}

const COLUNAS: { chave: keyof Posicao; rotulo: string; largura: number }[] = [
  { chave: "identificador", rotulo: "Posição", largura: 80 },
  { chave: "designacao", rotulo: "Designação", largura: 80 },
  { chave: "definicao", rotulo: "Definição", largura: 80 },
  { chave: "corrente", rotulo: "Corrente", largura: 70 },
  { chave: "funcao", rotulo: "Função", largura: 0 },
  { chave: "terminal", rotulo: "Terminal", largura: 60 },
  { chave: "cor", rotulo: "Cor", largura: 70 },
  { chave: "restricao", rotulo: "Restrição", largura: 90 },
]

/** Compara aparado, como a API grava. */
const longoDemais = (l: Posicao, chave: keyof Posicao) => l[chave].trim().length > TETO_DO_ITEM_DE_RELES[chave]

export type CampoLongo = { linha: number; designacao: string; rotulo: string; tamanho: number; maximo: number }

/**
 * Os campos que passam do teto, para a tela barrar antes de enviar — a API recusaria o lote
 * inteiro. Uma central real chega lá: a função do F21 da Duster tem 1.243 caracteres.
 */
export function camposLongosDemais(linhas: Posicao[]): CampoLongo[] {
  return linhas.flatMap((l, i) => COLUNAS.filter((c) => longoDemais(l, c.chave)).map((c) => ({
    linha: i + 1, designacao: l.designacao.trim(), rotulo: c.rotulo,
    tamanho: l[c.chave].trim().length, maximo: TETO_DO_ITEM_DE_RELES[c.chave],
  })))
}

const descreverLonga = (x: CampoLongo) =>
  `linha ${x.linha}${x.designacao ? ` (${x.designacao})` : ""}: ${x.rotulo} tem ${x.tamanho} caracteres; o máximo é ${x.maximo}`

export function LoteDePosicoes({ rota, posicaoInicial, invalidar, aoAvisar, aoIncluir, aoFechar }: {
  /** `…/caixas/{id}/itens/lote` */
  rota: string
  /** A posição que está no formulário de inclusão — quase sempre é a mesma do lote. */
  posicaoInicial: string
  invalidar: (string | number)[][]
  aoAvisar: (m: string | null) => void
  aoIncluir: (criadas: ItemDeReles[]) => void
  aoFechar: () => void
}) {
  const { criar } = usarGravar(invalidar)
  const [texto, setTexto] = useState("")
  const [nomeDoArquivo, setNomeDoArquivo] = useState("")
  const [ignoradas, setIgnoradas] = useState<LeituraDoTxt["ignoradas"]>([])
  const [linhas, setLinhas] = useState<Posicao[] | null>(null)
  const [posicao, setPosicao] = useState(posicaoInicial)
  const [aviso, setAviso] = useState<string | null>(null)

  function reconhecer(conteudo: string) {
    const leitura = lerLinhasDoTxt(conteudo)
    setIgnoradas(leitura.ignoradas)
    if (leitura.linhas.length === 0) {
      setLinhas(null)
      setAviso("Nenhuma linha no formato F01|10A|Função.")
      return
    }
    setAviso(null)
    setLinhas(leitura.linhas.map((l) => paraPosicao(l, posicao.trim())))
  }

  async function escolher(arquivo: File | undefined) {
    if (!arquivo) return
    try {
      const conteudo = await lerArquivoDeTexto(arquivo)
      setNomeDoArquivo(arquivo.name)
      setTexto(conteudo)
      reconhecer(conteudo)
    } catch (e) {
      setAviso(mensagem(e))
    }
  }

  const semPosicao = linhas?.filter((l) => !l.identificador.trim()).length ?? 0
  const longas = linhas ? camposLongosDemais(linhas) : []

  function incluir() {
    if (!linhas || linhas.length === 0 || semPosicao > 0 || longas.length > 0) return
    aoAvisar(null)
    criar.mutate({ rota, dados: { itens: linhas } }, {
      onSuccess: (criadas) => {
        const lista = (Array.isArray(criadas) ? criadas : []) as ItemDeReles[]
        aoAvisar(`${lista.length} ${lista.length === 1 ? "posição incluída" : "posições incluídas"}`
          + (nomeDoArquivo ? ` de ${nomeDoArquivo}.` : "."))
        aoIncluir(lista)
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  if (!linhas) {
    return (
      <div>
        <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--txt2)", lineHeight: 1.5, maxWidth: "80ch" }}>
          Uma posição por linha, no formato <code className="mono">F01|10A|Buzina esquerda</code>{" "}
          — código, corrente e função. Linhas fora do formato (títulos, cabeçalho) são ignoradas,
          e a tela diz quais. Nada é gravado antes de você conferir.
        </p>
        <input
          type="file"
          accept=".txt,text/plain"
          aria-label="Arquivo .txt"
          onChange={(e) => { void escolher(e.target.files?.[0]); e.target.value = "" }}
          style={{ fontSize: 11.5 }}
        />
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Ou cole o texto"
          placeholder={"ou cole o texto aqui\nF01|10A|Buzina esquerda\nF02|15A|Farol alto direito"}
          rows={6}
          style={{
            display: "block", width: "100%", marginTop: 8, padding: "6px 8px", fontSize: 11.5,
            fontFamily: "var(--fm)", border: "1px solid var(--line)", borderRadius: 5,
            background: "var(--surface)", color: "var(--txt)", resize: "vertical",
          }}
        />
        {aviso && <p role="alert" style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>{aviso}</p>}
        <Ignoradas ignoradas={ignoradas} />
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <Botao destaque desligado={!texto.trim()} aoClicar={() => { setNomeDoArquivo(""); reconhecer(texto) }}>
            Ler o texto
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </div>
    )
  }

  const mudar = (i: number, chave: keyof Posicao, valor: string) =>
    setLinhas((ls) => ls!.map((l, j) => (j === i ? { ...l, [chave]: valor } : l)))

  return (
    <div>
      <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--txt2)" }}>
        <b>{linhas.length} {linhas.length === 1 ? "linha reconhecida" : "linhas reconhecidas"}</b>
        {nomeDoArquivo && <> em <span className="mono">{nomeDoArquivo}</span></>}
        {ignoradas.length > 0 && <>; {ignoradas.length} ignorada{ignoradas.length === 1 ? "" : "s"}</>}.
        Confira e corrija antes de incluir.
      </p>
      <Ignoradas ignoradas={ignoradas} />

      <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "8px 0" }}>
        <label style={{ fontSize: 11.5, color: "var(--txt2)" }} htmlFor="lote-posicao">Posição</label>
        <input
          id="lote-posicao"
          value={posicao}
          onChange={(e) => setPosicao(e.target.value)}
          placeholder="2413"
          style={{ width: 110, padding: "4px 7px", fontSize: 12, border: "1px solid var(--line)",
                   borderRadius: 5, background: "var(--surface)", color: "var(--txt)" }}
        />
        <Botao
          desligado={!posicao.trim()}
          titulo="Preenche a posição das linhas que estão sem — as que você já mudou ficam como estão"
          aoClicar={() => setLinhas((ls) => ls!.map((l) =>
            l.identificador.trim() ? l : { ...l, identificador: posicao.trim() }))}
        >
          Replicar nas vazias
        </Botao>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid var(--line2)", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
          <thead>
            <tr>
              {COLUNAS.map((c) => (
                <th key={c.chave} style={{
                  textAlign: "left", padding: "5px 6px", fontSize: 10.5, fontWeight: 600,
                  color: "var(--txt2)", borderBottom: "1px solid var(--line)",
                  width: c.largura || undefined, whiteSpace: "nowrap",
                }}>{c.rotulo}</th>
              ))}
              <th style={{ width: 30, borderBottom: "1px solid var(--line)" }} />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i} style={{ borderBottom: "1px solid var(--line2)" }}>
                {COLUNAS.map((c) => (
                  <td key={c.chave} style={{ padding: "2px 3px" }}>
                    <input
                      value={l[c.chave]}
                      onChange={(e) => mudar(i, c.chave, e.target.value)}
                      aria-label={`${c.rotulo} da linha ${i + 1}`}
                      style={{
                        width: "100%", minWidth: c.chave === "funcao" ? 180 : 0, padding: "3px 5px",
                        fontSize: 11.5, borderRadius: 4, background: "var(--surface)", color: "var(--txt)",
                        border: "1px solid " + ((c.chave === "identificador" && !l.identificador.trim())
                          || longoDemais(l, c.chave) ? "var(--erro)" : "var(--line2)"),
                      }}
                    />
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <button
                    onClick={() => setLinhas((ls) => ls!.filter((_, j) => j !== i))}
                    aria-label={`Remover a linha ${i + 1}`}
                    title="Não incluir esta linha"
                    style={{ border: 0, background: "transparent", cursor: "pointer",
                             color: "var(--txt3)", fontSize: 13, lineHeight: 1 }}
                  >×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {semPosicao > 0 && (
        <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
          {semPosicao} {semPosicao === 1 ? "linha está" : "linhas estão"} sem posição. Preencha ou
          use “Replicar nas vazias”.
        </p>
      )}
      {longas.length > 0 && (
        <div role="alert" style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
          Texto longo demais — encurte antes de incluir:
          <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
            {longas.slice(0, 10).map((x) => <li key={`${x.linha}-${x.rotulo}`}>{descreverLonga(x)}</li>)}
            {longas.length > 10 && <li>… e mais {longas.length - 10}</li>}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <Botao destaque ocupado={criar.isPending}
               desligado={linhas.length === 0 || semPosicao > 0 || longas.length > 0} aoClicar={incluir}>
          {criar.isPending ? "Incluindo…" : `Incluir ${linhas.length} ${linhas.length === 1 ? "posição" : "posições"}`}
        </Botao>
        <Botao aoClicar={() => { setLinhas(null); setIgnoradas([]); setAviso(null) }}>Ler outro texto</Botao>
        <Botao aoClicar={aoFechar}>Cancelar</Botao>
      </div>
    </div>
  )
}

function Ignoradas({ ignoradas }: { ignoradas: LeituraDoTxt["ignoradas"] }) {
  if (ignoradas.length === 0) return null
  return (
    <details style={{ margin: "6px 0", fontSize: 11 }}>
      <summary style={{ cursor: "pointer", color: "var(--txt2)" }}>
        {ignoradas.length} {ignoradas.length === 1 ? "linha ignorada" : "linhas ignoradas"} — fora do formato
      </summary>
      <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "var(--txt3)", fontFamily: "var(--fm)" }}>
        {ignoradas.slice(0, 50).map((l) => (
          <li key={l.numero}>linha {l.numero}: {l.texto}</li>
        ))}
        {ignoradas.length > 50 && <li>… e mais {ignoradas.length - 50}</li>}
      </ul>
    </details>
  )
}
